import React, { FC, ReactNode } from 'react';
import { Link } from 'react-router';
import { PersonFill } from 'react-bootstrap-icons';
import { SiteManifest, SiteManifestPage } from './types';

type Language = SiteManifest['language'];

/**
 * Building blocks of the custom frontends (`src/sites`). Only the sites import
 * them, so they ship in the site chunks. Each site styles them under its own
 * root class (`.site-<name> .cf-faq`, ...).
 */

interface HeroImageProps {
  page: SiteManifestPage;
  className?: string;
}

/**
 * The page's hero from its manifest entry. `src`, `srcSet` and `sizes` match
 * the `<link rel="preload">` PHP renders, so the browser uses the preloaded
 * image instead of fetching another one.
 */
export const HeroImage: FC<HeroImageProps> = ({ page, className }) => (
  <img
    className={className}
    src={page.image}
    srcSet={page.imageSrcset}
    sizes={page.imageSrcset ? (page.imageSizes ?? '100vw') : undefined}
    width={page.imageWidth}
    height={page.imageHeight}
    alt={page.imageAlt}
    fetchPriority="high"
    decoding="async"
  />
);

export interface FaqItem {
  q: string;
  a: ReactNode;
}

interface FaqProps {
  title: string;
  items: FaqItem[];
  className?: string;
}

/** Questions as `<details>`: no script needed, every answer is in the page. */
export const Faq: FC<FaqProps> = ({ title, items, className }) => (
  <section className={`cf-faq ${className ?? ''}`} aria-labelledby="cf-faq-title">
    <h2 id="cf-faq-title">{title}</h2>
    <div className="cf-faq-list">
      {items.map((item) => (
        <details className="cf-faq-item" key={item.q}>
          <summary><h3 className="cf-faq-q">{item.q}</h3></summary>
          <div className="cf-faq-a">{item.a}</div>
        </details>
      ))}
    </div>
  </section>
);

interface Crumb {
  label: string;
  to?: string;
}

/** Home > City, matching the BreadcrumbList PHP renders as JSON-LD. */
export const Breadcrumbs: FC<{ items: Crumb[] }> = ({ items }) => (
  <nav className="cf-breadcrumbs" aria-label="Breadcrumb">
    <ol>
      {items.map((item) => (
        <li key={item.label}>
          {item.to ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

const ICON_LABEL: Partial<Record<Language, string>> = { en: 'person icon', nl: 'persoonsicoon', de: 'Personensymbol' };

/** The person icon that marks a fictional profile, as on the profile pages. */
export const FictionalIcon: FC<{ language?: Language }> = ({ language = 'en' }) => (
  <PersonFill className="cf-fictional-icon" aria-label={ICON_LABEL[language] ?? ICON_LABEL.en} role="img" />
);

/**
 * The disclosure every custom page carries near the footer, in the site's
 * language (`site.json` `language`; a language without its own text gets the
 * English one).
 */
export const Disclosure: FC<{ name: string; language?: Language }> = ({ name, language = 'en' }) => (
  <p className="cf-disclosure">
    {language === 'de' ? (
      <>
        {name} ist ein Unterhaltungsangebot für Erwachsene (18+). Wir nutzen fiktive Profile, deren Ziel es ist,
        mit Nutzern zu flirten; du erkennst sie an einem Personensymbol <FictionalIcon language="de" />, und
        physischer Kontakt mit diesen Profilen ist nicht möglich. Die Anmeldung ist kostenlos und deine ersten
        Nachrichten sind gratis; danach kosten Nachrichten, Flirts und Fotos Credits.
      </>
    ) : language === 'nl' ? (
      <>
        {name} is een entertainmentdienst voor volwassenen (18+). We gebruiken fictieve profielen die als doel
        hebben om met gebruikers te flirten; je herkent ze aan een persoonsicoon <FictionalIcon language="nl" /> en
        fysiek contact met deze profielen is niet mogelijk. Aanmelden is gratis en je eerste berichten zijn gratis;
        daarna kosten berichten, flirts en foto’s credits.
      </>
    ) : (
      <>
        {name} is an 18+ entertainment service. It uses fictional profiles whose purpose is flirting with
        users; they are marked with a person icon <FictionalIcon /> and physical contact with them is not
        possible. Registration is free and your first messages are free; after that, messages, flirts and
        photos cost credits.
      </>
    )}
  </p>
);
